import {FC, ReactNode, useEffect, useState} from 'react'
import {gql} from '@apollo/client'
import {Controller, FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {omit} from 'lodash'
import Button from 'jbc-front/components/Button'
import {useLazyQuery, useMutation, useQuery} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ProcedureContext, ReportFormProps} from './ReportForm'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {Panel, Body, Head} from 'components/ui/Panel'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {DateField} from 'components/reactHookForm/DateField'
import {Label} from 'components/forms/Label'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import styles from './R512Report.scss'
import {dateFormat} from 'libs/formatter'
import {isAdminSelector} from 'libs/redux'
import {InputWithText} from 'jbc-front/components/presenters/form/InputWithText'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Note} from 'jbc-front/components/presenters/ui/Note'
import ActionButton from 'jbc-front/components/ActionButton'
import {BoxDouble} from 'components/ui/BoxDouble'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {useSelector} from 'hooks/redux'
import {useNotify} from 'hooks/useNotify'
import {BankSearchModal, Bank, BankBranch} from 'components/modals/BankSearchModal'
import {useDispatch} from 'react-redux'
import {asyncError} from 'store/actions/asyncError'
import {schema as r507Schema, APPLICATION_TMP_DATA_NAME as R507_APPLICATION_TMP_DATA_NAME} from './R507Report'

const APPLICATION_TMP_DATA_NAME = 'r512_param_set'

const R512_REPORT = gql`
  query R512Report($procedureStatusId: ID!, $isAdmin: Boolean!, $loadApplication: Boolean!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        employee {
          id
          staffCode
          displayFullName
        }
        procedureChildcareDatum {
          id
          startedOn
          expectedBirthOn
          procedureChildcareChildDatum {
            id
            birthDate
          }
        }
        r512ParamSet {
          id
          startedOn
          birthDate
          expectedBirthOn
          reasonForPaymentParentalLeaveSupportAllowance
          spouseInsurancePersonNumber0
          spouseInsurancePersonNumber1
          spouseInsurancePersonNumber2
          spouseChildcareLeaveStartOn
          spouseState
          requestUsePublicMoneyReceivingAccount
          bankCode
          bankName
          bankBranchCode
          bankBranchName
          bankBranchType
          bankNumber
          japanPostBank
          japanPostBankCode
          japanPostBankNumber
          version
          createdAt
          needUpgrade
        }
        r512ParamSetForAdmin: r512ParamSet @include(if: $isAdmin) {
          id
          submitOn
          ownerCertifiedOn
          isApplicantConsented
          ignoreMyNumber
          startOn1
          endOn1
          workDays1
          workHours1
          salaryAmount1
          startOn2
          endOn2
          workDays2
          workHours2
          salaryAmount2
          hasCommutingExpenses
          commutingExpensePaymentDurationUnit
          commutingExpensePaymentDurationUnitOtherDetail
          notes
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
        }
        application: procedureTmpDatum(type: application, name: "${APPLICATION_TMP_DATA_NAME}") @include(if: $loadApplication) {
          id
          type
          name
          data
        }
        applicationChildcare: procedureTmpDatum(type: application, name: "${R507_APPLICATION_TMP_DATA_NAME}") @include(if: $loadApplication) {
          id
          type
          name
          data
        }
      }
      employeeFieldGroups {
        id
        code
        permission
      }
    }
  }
`

interface R512ReportQueryVariables {
  procedureStatusId: string
  isAdmin: boolean
  loadApplication: boolean
}

interface R512ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
      }
      procedureChildcareDatum?: {
        id: string
        startedOn: string
        expectedBirthOn?: string
        procedureChildcareChildDatum?: {
          id: string
          birthDate?: string
        }
      }
      r512ParamSet: {
        id?: string
        startedOn?: string
        birthDate?: string
        expectedBirthOn?: string
        reasonForPaymentParentalLeaveSupportAllowance?: string
        spouseInsurancePersonNumber0?: string
        spouseInsurancePersonNumber1?: string
        spouseInsurancePersonNumber2?: string
        spouseChildcareLeaveStartOn?: string
        spouseState?: string
        requestUsePublicMoneyReceivingAccount: boolean
        bankCode: string
        bankName: string
        bankBranchCode: string
        bankBranchName: string
        bankBranchType?: 'main' | 'branch'
        bankNumber: string
        japanPostBank: boolean
        japanPostBankCode?: string
        japanPostBankNumber?: string
        version: number
        createdAt?: string
        needUpgrade: boolean
      }
      r512ParamSetForAdmin?: {
        id?: string
        submitOn?: string
        ownerCertifiedOn?: string
        isApplicantConsented?: boolean
        ignoreMyNumber?: boolean
        startOn1?: string
        endOn1?: string
        workDays1?: number
        workHours1?: number
        salaryAmount1?: number
        startOn2?: string
        endOn2?: string
        workDays2?: number
        workHours2?: number
        salaryAmount2?: number
        hasCommutingExpenses: boolean
        commutingExpensePaymentDurationUnit?: 'per_month' | 'per_3months' | 'per_6months' | 'other'
        commutingExpensePaymentDurationUnitOtherDetail?: string
        notes: string
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
      }
      application?: {
        id: string
        type: string
        name: string
        data: unknown
      }
      applicationChildcare?: {
        id: string
        type: string
        name: string
        data: unknown
      }
    }
    employeeFieldGroups: {
      id: string
      code: string
      permission: 'updatable' | 'browsable' | 'not_display'
    }[]
  }
}

interface ChildcareCommonValues {
  startedOn: string
  birthDate: string
  expectedBirthOn: string
}

const makeChildcareCommonValues = (ps: R512ReportQueryResult['client']['procedureStatus']): ChildcareCommonValues => {
  const formatDate = (value?: string | null) => (value ? dateFormat(value, 'YYYY/MM/DD') : '')
  const childcare = ps.procedureChildcareDatum
  const r512 = ps.r512ParamSet

  if (childcare) {
    return {
      startedOn: formatDate(childcare.startedOn) || formatDate(r512?.startedOn),
      birthDate: formatDate(childcare.procedureChildcareChildDatum?.birthDate) || formatDate(r512?.birthDate),
      expectedBirthOn: formatDate(childcare.expectedBirthOn) || formatDate(r512?.expectedBirthOn),
    }
  }

  if (ps.applicationChildcare?.data) {
    try {
      const applicationChildcareData = r507Schema.validateSync(ps.applicationChildcare.data, {strict: true})
      return {
        startedOn: applicationChildcareData.childcareDatum.startedOn || '',
        birthDate: applicationChildcareData.childcareChildDatum.birthDate || '',
        expectedBirthOn: '',
      }
    } catch {
      // ignore
    }
  }

  return {
    startedOn: formatDate(r512?.startedOn),
    birthDate: formatDate(r512?.birthDate),
    expectedBirthOn: formatDate(r512?.expectedBirthOn),
  }
}

const R512_FETCH_EMPLOYEE_BANK_INFO = gql`
  query r512fetchEmployeeBankInfo($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        employee {
          id
          bankCode
          bankName
          bankBranchCode
          bankBranchName
          bankNumber
        }
      }
    }
  }
`

interface R512FetchEmployeeBankInfoQueryVariables {
  procedureStatusId: string
}

interface R512FetchEmployeeBankInfoQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      employee: {
        id: string
        bankCode?: string
        bankName?: string
        bankBranchCode?: string
        bankBranchName?: string
        bankNumber?: string
      }
    }
  }
}

const UPDATE_R512_PARAM_SET = gql`
  mutation UpdateR512ParamSet($input: UpdateR512ParamSetInput!) {
    updateR512ParamSet(input: $input) {
      r512ParamSet {
        id
      }
    }
  }
`

interface UpdateR512ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      startedOn: string
      birthDate?: string | null
      expectedBirthOn?: string | null
      submitOn?: string | null
      ownerCertifiedOn?: string | null
      isApplicantConsented?: boolean | null
      ignoreMyNumber?: boolean | null
      startOn1?: string | null
      endOn1?: string | null
      workDays1?: number | null
      workHours1?: number | null
      salaryAmount1?: number | null
      startOn2?: string | null
      endOn2?: string | null
      workDays2?: number | null
      workHours2?: number | null
      salaryAmount2?: number | null
      reasonForPaymentParentalLeaveSupportAllowance?: string | null
      spouseInsurancePersonNumber0?: string | null
      spouseInsurancePersonNumber1?: string | null
      spouseInsurancePersonNumber2?: string | null
      spouseChildcareLeaveStartOn?: string | null
      spouseState?: string | null
      requestUsePublicMoneyReceivingAccount?: boolean | null
      bankCode?: string | null
      bankName?: string | null
      bankBranchCode?: string | null
      bankBranchName?: string | null
      bankBranchType?: 'main' | 'branch' | null
      bankNumber?: string | null
      japanPostBank?: boolean | null
      japanPostBankCode?: string | null
      japanPostBankNumber?: string | null
      hasCommutingExpenses?: boolean | null
      commutingExpensePaymentDurationUnit?: string | null
      commutingExpensePaymentDurationUnitOtherDetail?: string | null
      notes?: string | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number | null
    }
  }
}

const UPDATE_APPLICATION_TMP_DATA = gql`
  mutation UpdateApplicationTmpData($input: UpdateProcedureTmpDatumInput!) {
    updateProcedureTmpDatum(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

interface UpdateApplicationTmpDataQueryVariables {
  input: {
    procedureStatusId: string
    type: 'application'
    name: typeof APPLICATION_TMP_DATA_NAME
    data: Schema
  }
}

const UPDATE_PROCEDURE_STATUS = gql`
  mutation updateProcedureStatus($input: UpdateProcedureStatusInput!) {
    updateProcedureStatus(input: $input) {
      procedureStatus {
        id
        dateOn
      }
    }
  }
`

interface UpdateProcedureStatusQueryVariables {
  input: {
    id: string
    dateOn?: string | null
    initialized?: boolean
  }
}

export const R512Report: FC<ReportFormProps> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  isConfirm,
  comments,
  renderReject,
}) => {
  const dispatch = useDispatch()
  const {data, loading} = useQuery<R512ReportQueryResult, R512ReportQueryVariables>(R512_REPORT, {
    variables: {
      procedureStatusId,
      isAdmin: procedureContext === 'admin',
      loadApplication: isConfirm || procedureContext === 'mypage',
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client?.procedureStatus
  const employeeFieldGroups = data?.client?.employeeFieldGroups || []

  const [updateR512ParamSet] = useMutation<unknown, UpdateR512ParamSetQueryVariables>(UPDATE_R512_PARAM_SET)
  const [updateApplicationTmpData] = useMutation<unknown, UpdateApplicationTmpDataQueryVariables>(
    UPDATE_APPLICATION_TMP_DATA
  )
  const [updateProcedureStatus] = useMutation<unknown, UpdateProcedureStatusQueryVariables>(UPDATE_PROCEDURE_STATUS)

  if (loading || !procedureStatus) {
    return null
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)
    const childcareCommonValues = makeChildcareCommonValues(procedureStatus)

    try {
      switch (procedureContext) {
        case 'admin':
          await updateR512ParamSet({
            variables: {
              input: {
                procedureStatusId,
                attributes: {
                  startedOn: childcareCommonValues.startedOn,
                  birthDate: blankToNull(childcareCommonValues.birthDate),
                  expectedBirthOn: blankToNull(childcareCommonValues.expectedBirthOn),
                  ...(data.paymentPeriod1 && {
                    startOn1: blankToNull(data.paymentPeriod1.startOn),
                    endOn1: blankToNull(data.paymentPeriod1.endOn),
                    workDays1: data.paymentPeriod1.workDays ? parseInt(data.paymentPeriod1.workDays, 10) : null,
                    workHours1: data.paymentPeriod1.workHours ? parseInt(data.paymentPeriod1.workHours, 10) : null,
                    salaryAmount1: data.paymentPeriod1.salaryAmount
                      ? parseInt(data.paymentPeriod1.salaryAmount, 10)
                      : null,
                  }),
                  ...(data.paymentPeriod2 && {
                    startOn2: blankToNull(data.paymentPeriod2.startOn),
                    endOn2: blankToNull(data.paymentPeriod2.endOn),
                    workDays2: data.paymentPeriod2.workDays ? parseInt(data.paymentPeriod2.workDays, 10) : null,
                    workHours2: data.paymentPeriod2.workHours ? parseInt(data.paymentPeriod2.workHours, 10) : null,
                    salaryAmount2: data.paymentPeriod2.salaryAmount
                      ? parseInt(data.paymentPeriod2.salaryAmount, 10)
                      : null,
                  }),
                  reasonForPaymentParentalLeaveSupportAllowance: blankToNull(
                    data.paymentParentalLeaveSupportAllowance.reasonForPaymentParentalLeaveSupportAllowance
                  ),
                  spouseInsurancePersonNumber0: data.paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0,
                  spouseInsurancePersonNumber1: data.paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1,
                  spouseInsurancePersonNumber2: data.paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2,
                  spouseChildcareLeaveStartOn: blankToNull(
                    data.paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn
                  ),
                  spouseState: blankToNull(data.paymentParentalLeaveSupportAllowance.spouseState),
                  requestUsePublicMoneyReceivingAccount:
                    data.directDepositForm.requestUsePublicMoneyReceivingAccount === 'true',
                  bankCode: data.directDepositForm.bankCode,
                  bankName: data.directDepositForm.bankName,
                  bankBranchCode: data.directDepositForm.bankBranchCode,
                  bankBranchName: data.directDepositForm.bankBranchName,
                  bankBranchType: data.directDepositForm.bankBranchType,
                  bankNumber: data.directDepositForm.bankNumber,
                  japanPostBank: data.directDepositForm.japanPostBank === 'true',
                  japanPostBankCode: data.directDepositForm.japanPostBankCode,
                  japanPostBankNumber: data.directDepositForm.japanPostBankNumber,
                  ...(data.remarks && {
                    hasCommutingExpenses: data.remarks.hasCommutingExpenses === 'true',
                    commutingExpensePaymentDurationUnit: blankToNull(data.remarks.commutingExpensePaymentDurationUnit),
                    commutingExpensePaymentDurationUnitOtherDetail:
                      data.remarks.commutingExpensePaymentDurationUnitOtherDetail,
                    notes: data.remarks.notes,

                    submitOn: blankToNull(data.remarks.submitOn),
                    ownerCertifiedOn: blankToNull(data.remarks.ownerCertifiedOn),
                    isApplicantConsented: !!data.remarks.isApplicantConsented,
                    ignoreMyNumber: !!data.remarks.ignoreMyNumber,
                  }),
                  ...(data.laborConsultantForm && {
                    needLaborConsultant: !!data.laborConsultantForm.needLaborConsultant,
                    laborConsultantCreateDate: blankToNull(data.laborConsultantForm.laborConsultantCreateDate),
                    laborConsultantName: data.laborConsultantForm.laborConsultantName,
                    laborConsultantAgentType: data.laborConsultantForm.laborConsultantAgentType,
                    laborConsultantPrefectureId: data.laborConsultantForm.laborConsultantPrefectureId,
                    laborConsultantPhoneNumber0: data.laborConsultantForm.laborConsultantPhoneNumber0,
                    laborConsultantPhoneNumber1: data.laborConsultantForm.laborConsultantPhoneNumber1,
                    laborConsultantPhoneNumber2: data.laborConsultantForm.laborConsultantPhoneNumber2,
                  }),
                  version: procedureStatus.r512ParamSet.version,
                },
              },
            },
          })
          await updateProcedureStatus({
            variables: {
              input: {
                id: procedureStatusId,
                initialized: true,
              },
            },
          })
          break
        case 'mypage': {
          const applicationData = {...data, ...childcareCommonValues}
          await updateApplicationTmpData({
            variables: {
              input: {
                procedureStatusId,
                type: 'application',
                name: APPLICATION_TMP_DATA_NAME,
                data: applicationData,
              },
            },
          })

          await updateProcedureStatus({
            variables: {
              input: {
                id: procedureStatusId,
                dateOn: childcareCommonValues.startedOn || null,
                initialized: true,
              },
            },
          })
          break
        }
        default:
          return procedureContext satisfies never // exhaustiveness check
      }
    } catch (err) {
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">出生時育休の手続き</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        submitText={submitText}
        onSubmit={onSubmit}
        procedureContext={procedureContext}
        employeeFieldGroups={employeeFieldGroups}
        comments={comments}
        renderReject={renderReject}
      />
    </div>
  )
}

const reasonForPaymentParentalLeaveSupportAllowances = [
  {value: '', label: ''},
  {
    value: 'insured_and_take_leave',
    label:
      '1. 配偶者が雇用保険被保険者であって、出生時育児休業給付金または育児休業給付金が支給される休業を一定の期間に14日以上取得した場合',
  },
  {
    value: 'public_officer_and_take_leave',
    label:
      '2. 配偶者が公務員（雇用保険被保険者である場合を除く。）であって、各種法律に基づく育児休業を一定の期間に14日以上取得した場合',
  },
  {value: 'not_leave', label: '3. 配偶者が、子の出生日の翌日において配偶者の育児休業を要件としない場合に該当する場合'},
]

const spouseStates = [
  {value: '', label: ''},
  {value: 'no_spouse', label: '1. 配偶者がいない'},
  {value: 'no_legal_relationship', label: '2. 配偶者が被保険者の子と法律上の親子関係がない'},
  {value: 'domestic_violence_living_separately', label: '3. 被保険者が配偶者から暴力を受け別居中'},
  {value: 'spouse_is_unemployed', label: '4. 配偶者が無業者'},
  {value: 'spouse_is_self_employed', label: '5. 配偶者が自営業やフリーランスなど雇用される労働者でない'},
  {value: 'spouse_is_on_maternity_leave', label: '6. 配偶者が産後休業中'},
  {value: 'other_reason', label: '7. 1～6以外の理由で配偶者が育児休業をすることができない'},
]

const commutingExpensePaymentDurationUnits = [
  {value: '', label: ''},
  {value: 'per_month', label: '毎月'},
  {value: 'per_3months', label: '3ヶ月'},
  {value: 'per_6months', label: '6ヶ月'},
  {value: 'other', label: 'その他'},
]

const paymentPeriodSchema = (prefix: string, dateRequired: boolean = false) =>
  yup.object({
    startOn: ((schema) => (dateRequired ? schema.required() : schema.notRequired()))(
      yup.string().dateFormat().label(`${prefix} 初日`)
    ),
    endOn: ((schema) => (dateRequired ? schema.required() : schema.notRequired()))(
      yup.string().dateFormat().label(`${prefix} 末日`)
    ),
    workDays: yup
      .string()
      .notRequired()
      .matches(/^\d{1,2}$/, {
        message: ({label}) => `${label}を2桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label(`${prefix} 就業日数`),
    workHours: yup
      .string()
      .notRequired()
      .matches(/^\d{1,3}$/, {
        message: ({label}) => `${label}を3桁以内の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label(`${prefix} 就業時間`),
    salaryAmount: yup
      .string()
      .notRequired()
      .numeric()
      .test(
        'salaryAmountMax',
        ({label}) => `${label}を9,999,999円以下で入力してください`,
        (value) => !value || parseInt(value, 10) <= 9999999
      )
      .label(`${prefix} 支払われた賃金額`),
  })

const schema = yup.object({
  startedOn: yup.string().required().dateFormat().label('育児休業等開始年月日'),
  birthDate: yup.string().notRequired().dateFormat().label('出産年月日'),
  expectedBirthOn: yup.string().notRequired().dateFormat().label('出産予定日'),
  paymentPeriod1: paymentPeriodSchema('支給単位期間その1', true)
    .nullable()
    .when('$procedureContext', {is: 'admin', then: (schema) => schema.required()}),
  paymentPeriod2: paymentPeriodSchema('支給単位期間その2')
    .nullable()
    .when('$procedureContext', {is: 'admin', then: (schema) => schema.required()}),
  paymentParentalLeaveSupportAllowance: yup.object({
    reasonForPaymentParentalLeaveSupportAllowance: yup.string().notRequired().label('出生後休業支援給付金の支給理由'),
    spouseInsurancePersonNumber0: yup
      .string()
      .notRequired()
      .numeric()
      .matches(/^\d{4}$/, {
        message: ({label}) => `${label}を4桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('配偶者の被保険者番号の1番'),
    spouseInsurancePersonNumber1: yup
      .string()
      .notRequired()
      .numeric()
      .matches(/^\d{6}$/, {
        message: ({label}) => `${label}を6桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('配偶者の被保険者番号の2番'),
    spouseInsurancePersonNumber2: yup
      .string()
      .notRequired()
      .numeric()
      .matches(/^\d$/, {
        message: ({label}) => `${label}を1桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .label('配偶者の被保険者番号の3番'),
    spouseChildcareLeaveStartOn: yup.string().notRequired().dateFormat().label('配偶者の育児休業開始年月日'),
    spouseState: yup.string().notRequired().label('配偶者の状態'),
  }),
  directDepositForm: yup.object({
    requestUsePublicMoneyReceivingAccount: yup
      .string()
      .notRequired()
      .oneOf(['true', 'false'])
      .label('公金受取口座利用希望'),
    bankCode: yup
      .string()
      .notRequired()
      .matches(/^\d{4}$/, {
        message: ({label}) => `${label}を4桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .test(
        'not-japan-post-bank-code',
        '払渡希望金融機関にゆうちょ銀行を指定する場合、金融機関種別でゆうちょ銀行を選択してください',
        (value) => value !== '9900'
      )
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('銀行コード'),
    bankName: yup
      .string()
      .notRequired()
      .max(255)
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('銀行名'),
    bankBranchCode: yup
      .string()
      .notRequired()
      .matches(/^\d{3}$/, {
        message: ({label}) => `${label}を3桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('支店コード'),
    bankBranchName: yup
      .string()
      .notRequired()
      .max(255)
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('支店名'),
    bankBranchType: yup.string().notRequired().oneOf(['main', 'branch']).label('本店支店区分'),
    bankNumber: yup
      .string()
      .notRequired()
      .numeric()
      .max(7, ({label}) => `${label}を半角数字7桁以下で入力してください`)
      .when('japanPostBank', {
        is: 'true',
        then: (schema) => schema.transform(() => null),
      })
      .label('口座番号'),
    japanPostBank: yup.string().notRequired().oneOf(['true', 'false']).label('金融機関種別'),
    japanPostBankCode: yup
      .string()
      .notRequired()
      .matches(/^\d{5}$/, {
        message: ({label}) => `${label}を5桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .when('japanPostBank', {
        is: 'false',
        then: (schema) => schema.transform(() => null),
      })
      .label('記号'),
    japanPostBankNumber: yup
      .string()
      .notRequired()
      .matches(/^\d{8}$/, {
        message: ({label}) => `${label}を8桁の半角数字で入力してください`,
        excludeEmptyString: true,
      })
      .when('japanPostBank', {
        is: 'false',
        then: (schema) => schema.transform(() => null),
      })
      .label('番号'),
  }),
  remarks: yup
    .object({
      hasCommutingExpenses: yup.string().notRequired().oneOf(['true', 'false']).label('通勤手当の有無'),
      commutingExpensePaymentDurationUnit: yup.string().notRequired().label('通勤手当の支給単位'),
      commutingExpensePaymentDurationUnitOtherDetail: yup
        .string()
        .notRequired()
        .max(4)
        .label('通勤手当の支給単位 その他の内容'),
      notes: yup.string().notRequired().max(68).label('備考'),
      ownerCertifiedOn: yup.string().notRequired().dateFormat().label('事業主証明年月日'),
      submitOn: yup.string().notRequired().dateFormat().label('提出年月日'),
      isApplicantConsented: yup.boolean().notRequired().label('申請について同意済み'),
      ignoreMyNumber: yup.boolean().notRequired().label('本人事由によりマイナンバー届出不可'),
    })
    .nullable()
    .when('$procedureContext', {is: 'admin', then: (schema) => schema.required()}),
  laborConsultantForm: laborConsultantSchema
    .nullable()
    .when('$procedureContext', {is: 'admin', then: (schema) => schema.required()}),
})

type Schema = yup.InferType<typeof schema>

const makeDefaultValues = (ps: R512ReportQueryResult['client']['procedureStatus']): Schema => {
  const radioBooleanFormat = (value: boolean | undefined | null): 'true' | 'false' | undefined => {
    if (value === undefined || value === null) return undefined
    return value ? 'true' : 'false'
  }

  if (ps.application?.data && !ps.r512ParamSet.createdAt) {
    const {application, ...withoutApplication} = ps
    const defaultValues = makeDefaultValues(withoutApplication)
    const applicationValues = omit(schema.validateSync(application.data, {strict: true}), [
      'startedOn',
      'birthDate',
      'expectedBirthOn',
      'paymentPeriod1',
      'paymentPeriod2',
      'remarks',
      'laborConsultantForm',
    ])
    return {...defaultValues, ...applicationValues}
  }

  const r512 = ps.r512ParamSet
  const r512Admin = ps.r512ParamSetForAdmin
  const childcareCommonValues = makeChildcareCommonValues(ps)

  return {
    ...childcareCommonValues,
    paymentPeriod1: r512Admin
      ? {
          startOn: r512Admin.startOn1 ? dateFormat(r512Admin.startOn1, 'YYYY/MM/DD') : '',
          endOn: r512Admin.endOn1 ? dateFormat(r512Admin.endOn1, 'YYYY/MM/DD') : '',
          workDays: String(r512Admin.workDays1 ?? ''),
          workHours: String(r512Admin.workHours1 ?? ''),
          salaryAmount: String(r512Admin.salaryAmount1 ?? ''),
        }
      : null,
    paymentPeriod2: r512Admin
      ? {
          startOn: r512Admin.startOn2 ? dateFormat(r512Admin.startOn2, 'YYYY/MM/DD') : '',
          endOn: r512Admin.endOn2 ? dateFormat(r512Admin.endOn2, 'YYYY/MM/DD') : '',
          workDays: String(r512Admin.workDays2 ?? ''),
          workHours: String(r512Admin.workHours2 ?? ''),
          salaryAmount: String(r512Admin.salaryAmount2 ?? ''),
        }
      : null,
    paymentParentalLeaveSupportAllowance: {
      reasonForPaymentParentalLeaveSupportAllowance: r512.reasonForPaymentParentalLeaveSupportAllowance,
      spouseInsurancePersonNumber0: r512.spouseInsurancePersonNumber0,
      spouseInsurancePersonNumber1: r512.spouseInsurancePersonNumber1,
      spouseInsurancePersonNumber2: r512.spouseInsurancePersonNumber2,
      spouseChildcareLeaveStartOn: r512.spouseChildcareLeaveStartOn
        ? dateFormat(r512.spouseChildcareLeaveStartOn, 'YYYY/MM/DD')
        : '',
      spouseState: r512.spouseState,
    },
    directDepositForm: {
      requestUsePublicMoneyReceivingAccount: radioBooleanFormat(r512.requestUsePublicMoneyReceivingAccount),
      bankCode: r512.bankCode,
      bankName: r512.bankName,
      bankBranchCode: r512.bankBranchCode,
      bankBranchName: r512.bankBranchName,
      bankBranchType: r512.bankBranchType,
      bankNumber: r512.bankNumber,
      japanPostBank: radioBooleanFormat(r512.japanPostBank),
      japanPostBankCode: r512.japanPostBankCode,
      japanPostBankNumber: r512.japanPostBankNumber,
    },
    remarks: r512Admin
      ? {
          hasCommutingExpenses: radioBooleanFormat(r512Admin.hasCommutingExpenses),
          commutingExpensePaymentDurationUnit: r512Admin.commutingExpensePaymentDurationUnit,
          commutingExpensePaymentDurationUnitOtherDetail: r512Admin.commutingExpensePaymentDurationUnitOtherDetail,
          notes: r512Admin.notes,
          ownerCertifiedOn: r512Admin.ownerCertifiedOn ? dateFormat(r512Admin.ownerCertifiedOn, 'YYYY/MM/DD') : '',
          submitOn: r512Admin.submitOn ? dateFormat(r512Admin.submitOn, 'YYYY/MM/DD') : '',
          isApplicantConsented: r512Admin.isApplicantConsented,
          ignoreMyNumber: r512Admin.ignoreMyNumber,
        }
      : null,
    laborConsultantForm: r512Admin
      ? {
          needLaborConsultant: r512Admin.needLaborConsultant,
          laborConsultantCreateDate: r512Admin.laborConsultantCreateDate
            ? dateFormat(r512Admin.laborConsultantCreateDate, 'YYYY/MM/DD')
            : '',
          laborConsultantName: r512Admin.laborConsultantName || '',
          laborConsultantAgentType: r512Admin.laborConsultantAgentType || undefined,
          laborConsultantPrefectureId: r512Admin.laborConsultantPrefecture?.id || undefined,
          laborConsultantPhoneNumber0: r512Admin.laborConsultantPhoneNumber0 || '',
          laborConsultantPhoneNumber1: r512Admin.laborConsultantPhoneNumber1 || '',
          laborConsultantPhoneNumber2: r512Admin.laborConsultantPhoneNumber2 || '',
        }
      : null,
  }
}

interface FormProps {
  procedureStatus: R512ReportQueryResult['client']['procedureStatus']
  submitText: string
  onSubmit: SubmitHandler<Schema>
  procedureContext: ProcedureContext
  employeeFieldGroups: R512ReportQueryResult['client']['employeeFieldGroups']
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
}

const Form: FC<FormProps> = ({
  procedureStatus,
  submitText,
  onSubmit,
  procedureContext,
  employeeFieldGroups,
  comments,
  renderReject,
}) => {
  const notify = useNotify()
  const isAdmin = useSelector(isAdminSelector)
  const [isOpenBankSearchModal, setIsOpenBankSearchModal] = useState(false)

  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureContext},
  })
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: {errors, isSubmitting},
  } = methods

  const reasonForPaymentParentalLeaveSupportAllowance = watch(
    'paymentParentalLeaveSupportAllowance.reasonForPaymentParentalLeaveSupportAllowance'
  )
  const isJapanPostBank = watch('directDepositForm.japanPostBank') === 'true'
  const hasCommutingExpenses = watch('remarks.hasCommutingExpenses') === 'true'
  const commutingExpensePaymentDurationUnit = watch('remarks.commutingExpensePaymentDurationUnit')

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r512ParamSet.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  useEffect(() => {
    const options = {shouldTouch: true, shouldDirty: true, shouldValidate: true}
    switch (reasonForPaymentParentalLeaveSupportAllowance) {
      case 'insured_and_take_leave':
        setValue('paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn', '', options)
        setValue('paymentParentalLeaveSupportAllowance.spouseState', '', options)
        break
      case 'public_officer_and_take_leave':
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseState', '', options)
        break
      case 'not_leave':
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn', '', options)
        break
      default:
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2', null, options)
        setValue('paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn', '', options)
        setValue('paymentParentalLeaveSupportAllowance.spouseState', '', options)
        break
    }
  }, [reasonForPaymentParentalLeaveSupportAllowance, setValue])

  useEffect(() => {
    if (!hasCommutingExpenses) {
      const options = {shouldTouch: true, shouldDirty: true, shouldValidate: true}
      setValue('remarks.commutingExpensePaymentDurationUnit', '', options)
      setValue('remarks.commutingExpensePaymentDurationUnitOtherDetail', '', options)
    }
  }, [hasCommutingExpenses, setValue])

  useEffect(() => {
    if (commutingExpensePaymentDurationUnit !== 'other') {
      setValue('remarks.commutingExpensePaymentDurationUnitOtherDetail', '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [commutingExpensePaymentDurationUnit, setValue])

  const bankInformationPermission = isAdmin
    ? 'updatable'
    : (employeeFieldGroups.find((efg) => efg.code === 'bank_infomation')?.permission ?? 'not_display')

  const [fetchEmployeeBankInfo, {loading: fetchingEmployeeBankInfo}] = useLazyQuery<
    R512FetchEmployeeBankInfoQueryResult,
    R512FetchEmployeeBankInfoQueryVariables
  >(R512_FETCH_EMPLOYEE_BANK_INFO, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  const handleSelectBank = (bank: Bank, bankBranch: BankBranch) => {
    setValue('directDepositForm.bankCode', bank.bankCode, {shouldTouch: true, shouldDirty: true, shouldValidate: true})
    setValue('directDepositForm.bankName', bank.name, {shouldTouch: true, shouldDirty: true, shouldValidate: true})
    setValue('directDepositForm.bankBranchCode', bankBranch.branchCode, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    })
    setValue('directDepositForm.bankBranchName', bankBranch.name, {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    })
  }

  const handleCopyFromEmployeeBankInfo = async () => {
    const res = await fetchEmployeeBankInfo({
      variables: {procedureStatusId: procedureStatus.id},
    })
    if (res.data) {
      const {bankCode, bankName, bankBranchCode, bankBranchName, bankNumber} = res.data.client.procedureStatus.employee
      if ([bankCode, bankName, bankBranchCode, bankBranchName, bankName].every((v) => !v)) {
        notify('従業員情報に口座情報が登録されていないため、データをコピーできません。', 'error')
        return
      }

      setValue('directDepositForm.bankCode', bankCode || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankName', bankName || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankBranchCode', bankBranchCode || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankBranchName', bankBranchName || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('directDepositForm.bankNumber', bankNumber || '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} />
        {comments}

        <Panel>
          <Head>雇用保険育児休業給付受給資格確認票・出生時育児休業給付金／出生後休業支援給付金支給申請書</Head>
          <Body className={styles.formBody}>
            {procedureContext === 'admin' && (
              <>
                <Panel>
                  <Head>支給単位期間その1</Head>
                  <Body className={styles.formBody}>
                    <DateField
                      name="paymentPeriod1.startOn"
                      label="初日"
                      error={errors.paymentPeriod1?.startOn?.message}
                      required
                    />
                    <DateField
                      name="paymentPeriod1.endOn"
                      label="末日"
                      error={errors.paymentPeriod1?.endOn?.message}
                      required
                    />
                    <div className={styles.field}>
                      <Label>就業日数</Label>
                      <InputWithText
                        {...register('paymentPeriod1.workDays')}
                        text="日"
                        isError={!!errors.paymentPeriod1?.workDays}
                      />
                      {errors.paymentPeriod1?.workDays?.message && (
                        <ErrorMessage>{errors.paymentPeriod1.workDays.message}</ErrorMessage>
                      )}
                    </div>
                    <div className={styles.field}>
                      <Label>就業時間</Label>
                      <InputWithText
                        {...register('paymentPeriod1.workHours')}
                        text="時間"
                        isError={!!errors.paymentPeriod1?.workHours}
                      />
                      {errors.paymentPeriod1?.workHours?.message && (
                        <ErrorMessage>{errors.paymentPeriod1.workHours.message}</ErrorMessage>
                      )}
                    </div>
                    <div className={styles.field}>
                      <Label>支払われた賃金額</Label>
                      <InputWithText
                        {...register('paymentPeriod1.salaryAmount')}
                        text="円"
                        isError={!!errors.paymentPeriod1?.salaryAmount}
                      />
                      {errors.paymentPeriod1?.salaryAmount?.message && (
                        <ErrorMessage>{errors.paymentPeriod1.salaryAmount.message}</ErrorMessage>
                      )}
                    </div>
                  </Body>
                </Panel>

                <Panel>
                  <Head>支給単位期間その2</Head>
                  <Body className={styles.formBody}>
                    <DateField
                      name="paymentPeriod2.startOn"
                      label="初日"
                      error={errors.paymentPeriod2?.startOn?.message}
                    />
                    <DateField name="paymentPeriod2.endOn" label="末日" error={errors.paymentPeriod2?.endOn?.message} />
                    <div className={styles.field}>
                      <Label>就業日数</Label>
                      <InputWithText
                        {...register('paymentPeriod2.workDays')}
                        text="日"
                        isError={!!errors.paymentPeriod2?.workDays}
                      />
                      {errors.paymentPeriod2?.workDays?.message && (
                        <ErrorMessage>{errors.paymentPeriod2.workDays.message}</ErrorMessage>
                      )}
                    </div>
                    <div className={styles.field}>
                      <Label>就業時間</Label>
                      <InputWithText
                        {...register('paymentPeriod2.workHours')}
                        text="時間"
                        isError={!!errors.paymentPeriod2?.workHours}
                      />
                      {errors.paymentPeriod2?.workHours?.message && (
                        <ErrorMessage>{errors.paymentPeriod2.workHours.message}</ErrorMessage>
                      )}
                    </div>
                    <div className={styles.field}>
                      <Label>支払われた賃金額</Label>
                      <InputWithText
                        {...register('paymentPeriod2.salaryAmount')}
                        text="円"
                        isError={!!errors.paymentPeriod2?.salaryAmount}
                      />
                      {errors.paymentPeriod2?.salaryAmount?.message && (
                        <ErrorMessage>{errors.paymentPeriod2.salaryAmount.message}</ErrorMessage>
                      )}
                    </div>
                  </Body>
                </Panel>
              </>
            )}

            <Panel>
              <Head>出生後休業支援給付金の支給申請を行う場合の記載事項</Head>
              <Body className={styles.formBody}>
                <div className={styles.field}>
                  <Label>支給理由の選択</Label>
                  <Controller
                    control={control}
                    name="paymentParentalLeaveSupportAllowance.reasonForPaymentParentalLeaveSupportAllowance"
                    render={({field: {onChange, onBlur, value}}) => (
                      <Select
                        options={reasonForPaymentParentalLeaveSupportAllowances}
                        onChange={({value}) => onChange(value)}
                        onBlur={onBlur}
                        value={reasonForPaymentParentalLeaveSupportAllowances.find((option) => option.value === value)}
                        placeholder="選択してください"
                      />
                    )}
                  />
                </div>
                <div className={styles.field}>
                  <Label>配偶者の被保険者番号</Label>
                  <div className={styles.personNumber}>
                    <Input
                      className={styles.personNumberSegment1}
                      {...register('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber0')}
                      maxLength={4}
                      isError={!!errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber0}
                      disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'insured_and_take_leave'}
                    />
                    -
                    <Input
                      className={styles.personNumberSegment2}
                      {...register('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber1')}
                      maxLength={6}
                      isError={!!errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber1}
                      disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'insured_and_take_leave'}
                    />
                    -
                    <Input
                      className={styles.personNumberSegment3}
                      {...register('paymentParentalLeaveSupportAllowance.spouseInsurancePersonNumber2')}
                      maxLength={1}
                      isError={!!errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber2}
                      disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'insured_and_take_leave'}
                    />
                  </div>
                  {(() => {
                    const personNumberErrors = [
                      errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber0?.message,
                      errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber1?.message,
                      errors.paymentParentalLeaveSupportAllowance?.spouseInsurancePersonNumber2?.message,
                    ].filter((v) => v !== undefined)
                    return (
                      personNumberErrors.length > 0 && (
                        <div>
                          {personNumberErrors.map((error, index) => (
                            <ErrorMessage key={index}>{error}</ErrorMessage>
                          ))}
                        </div>
                      )
                    )
                  })()}
                </div>
                <DateField
                  name="paymentParentalLeaveSupportAllowance.spouseChildcareLeaveStartOn"
                  label="配偶者の育児休業開始年月日"
                  error={errors.paymentParentalLeaveSupportAllowance?.spouseChildcareLeaveStartOn?.message}
                  disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'public_officer_and_take_leave'}
                />
                <div className={styles.field}>
                  <Label>配偶者の状態</Label>
                  <Controller
                    control={control}
                    name="paymentParentalLeaveSupportAllowance.spouseState"
                    render={({field: {onChange, onBlur, value}}) => (
                      <Select
                        options={spouseStates}
                        onChange={({value}) => onChange(value)}
                        onBlur={onBlur}
                        value={spouseStates.find((option) => option.value === value)}
                        placeholder="選択してください"
                        disabled={reasonForPaymentParentalLeaveSupportAllowance !== 'not_leave'}
                      />
                    )}
                  />
                </div>
              </Body>
            </Panel>

            <Panel>
              <Head>払渡希望金融機関指定届</Head>
              <Body className={styles.formBody}>
                <div className={styles.field}>
                  <Label>公金受取口座利用希望</Label>
                  <RadioContainer>
                    <Radio {...register('directDepositForm.requestUsePublicMoneyReceivingAccount')} value="false">
                      希望しない
                    </Radio>
                    <Radio {...register('directDepositForm.requestUsePublicMoneyReceivingAccount')} value="true">
                      希望する
                    </Radio>
                  </RadioContainer>
                  <p className={styles.noticeRed}>
                    マイナポータルに登録されている公金受取口座への振込を新たに希望される場合は「希望する」を選択してください。
                    <br />
                    「希望する」を選択した場合は、以下の払渡希望金融機関の入力が不要ですが、入力された場合は入力された払渡希望金融機関への振込が優先されます。
                  </p>
                </div>
                <div className={styles.field}>
                  <Label>金融機関種別</Label>
                  <RadioContainer>
                    <Radio {...register('directDepositForm.japanPostBank')} value="false">
                      ゆうちょ銀行以外
                    </Radio>
                    <Radio {...register('directDepositForm.japanPostBank')} value="true">
                      ゆうちょ銀行
                    </Radio>
                  </RadioContainer>
                  <p className={styles.noticeRed}>
                    払渡希望金融機関にゆうちょ銀行を指定する場合は記号（5桁）番号（8桁）の形式で入力してください。
                  </p>
                </div>
                {isJapanPostBank ? (
                  <>
                    <BoxDouble>
                      <div className={styles.field}>
                        <Label>記号</Label>
                        <Input
                          {...register('directDepositForm.japanPostBankCode')}
                          isError={!!errors.directDepositForm?.japanPostBankCode}
                          maxLength={5}
                        />
                        {errors.directDepositForm?.japanPostBankCode?.message && (
                          <ErrorMessage>{errors.directDepositForm.japanPostBankCode.message}</ErrorMessage>
                        )}
                      </div>
                      <div className={styles.field}>
                        <Label>番号</Label>
                        <Input
                          {...register('directDepositForm.japanPostBankNumber')}
                          isError={!!errors.directDepositForm?.japanPostBankNumber}
                          maxLength={8}
                        />
                        {errors.directDepositForm?.japanPostBankNumber?.message && (
                          <ErrorMessage>{errors.directDepositForm.japanPostBankNumber.message}</ErrorMessage>
                        )}
                      </div>
                    </BoxDouble>
                    <p className={styles.notice}>
                      店名または店番・口座番号から記号・番号を検索したい場合は
                      <a
                        className="u-txt-link"
                        href="https://www.jp-bank.japanpost.jp/kojin/sokin/furikomi/kouza/kj_sk_fm_kz_2.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        こちら
                      </a>
                    </p>
                  </>
                ) : (
                  <>
                    <div>
                      <ActionButton className="u-mr20" onClick={() => setIsOpenBankSearchModal(true)}>
                        銀行コード検索
                      </ActionButton>
                      {bankInformationPermission !== 'not_display' && (
                        <ActionButton onClick={handleCopyFromEmployeeBankInfo} disabled={fetchingEmployeeBankInfo}>
                          従業員情報からコピー
                        </ActionButton>
                      )}

                      <BankSearchModal
                        isOpen={isOpenBankSearchModal}
                        hideModal={() => setIsOpenBankSearchModal(false)}
                        onSelect={handleSelectBank}
                      />
                    </div>
                    <BoxDouble>
                      <div className={styles.field}>
                        <Label>銀行コード</Label>
                        <Input
                          {...register('directDepositForm.bankCode')}
                          isError={!!errors.directDepositForm?.bankCode}
                        />
                        {errors.directDepositForm?.bankCode?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankCode.message}</ErrorMessage>
                        )}
                      </div>
                      <div className={styles.field}>
                        <Label>銀行名</Label>
                        <Input
                          {...register('directDepositForm.bankName')}
                          isError={!!errors.directDepositForm?.bankName}
                        />
                        {errors.directDepositForm?.bankName?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankName.message}</ErrorMessage>
                        )}
                      </div>
                    </BoxDouble>
                    <BoxDouble>
                      <div className={styles.field}>
                        <Label>支店コード</Label>
                        <Input
                          {...register('directDepositForm.bankBranchCode')}
                          isError={!!errors.directDepositForm?.bankBranchCode}
                        />
                        {errors.directDepositForm?.bankBranchCode?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankBranchCode.message}</ErrorMessage>
                        )}
                      </div>
                      <div className={styles.field}>
                        <Label>支店名</Label>
                        <Input
                          {...register('directDepositForm.bankBranchName')}
                          isError={!!errors.directDepositForm?.bankBranchName}
                        />
                        {errors.directDepositForm?.bankBranchName?.message && (
                          <ErrorMessage>{errors.directDepositForm.bankBranchName.message}</ErrorMessage>
                        )}
                      </div>
                    </BoxDouble>
                    <div className={styles.field}>
                      <Label>本店支店区分</Label>
                      <RadioContainer>
                        <Radio {...register('directDepositForm.bankBranchType')} value="main">
                          本店
                        </Radio>
                        <Radio {...register('directDepositForm.bankBranchType')} value="branch">
                          支店
                        </Radio>
                      </RadioContainer>
                    </div>
                    <div className={styles.field}>
                      <Label>口座番号</Label>
                      <Input
                        {...register('directDepositForm.bankNumber')}
                        isError={!!errors.directDepositForm?.bankNumber}
                      />
                      {errors.directDepositForm?.bankNumber?.message && (
                        <ErrorMessage>{errors.directDepositForm.bankNumber.message}</ErrorMessage>
                      )}
                    </div>
                  </>
                )}
              </Body>
            </Panel>

            {procedureContext === 'admin' && (
              <>
                <Panel>
                  <Head>備考</Head>
                  <Body className={styles.formBody}>
                    <Panel>
                      <Head>通勤手当</Head>
                      <Body className={styles.formBody}>
                        <div className={styles.field}>
                          <Label>通勤手当の有無</Label>
                          <RadioContainer>
                            <Radio {...register('remarks.hasCommutingExpenses')} value="true">
                              あり
                            </Radio>
                            <Radio {...register('remarks.hasCommutingExpenses')} value="false">
                              なし
                            </Radio>
                          </RadioContainer>
                        </div>
                        <div className={styles.field}>
                          <Label>通勤手当の支給単位</Label>
                          <Controller
                            control={control}
                            name="remarks.commutingExpensePaymentDurationUnit"
                            render={({field: {onChange, onBlur, value}}) => (
                              <Select
                                options={commutingExpensePaymentDurationUnits}
                                onChange={({value}) => onChange(value)}
                                onBlur={onBlur}
                                value={commutingExpensePaymentDurationUnits.find((option) => option.value === value)}
                                placeholder="選択してください"
                                disabled={watch('remarks.hasCommutingExpenses') == 'false'}
                              />
                            )}
                          />
                        </div>
                        <div className={styles.field}>
                          <Label>通勤手当のその他の内容</Label>
                          <Input
                            {...register('remarks.commutingExpensePaymentDurationUnitOtherDetail')}
                            isError={!!errors.remarks?.commutingExpensePaymentDurationUnitOtherDetail}
                            disabled={watch('remarks.commutingExpensePaymentDurationUnit') !== 'other'}
                          />
                          {errors.remarks?.commutingExpensePaymentDurationUnitOtherDetail?.message && (
                            <ErrorMessage>
                              {errors.remarks.commutingExpensePaymentDurationUnitOtherDetail.message}
                            </ErrorMessage>
                          )}
                        </div>
                      </Body>
                    </Panel>

                    <div className={styles.field}>
                      <Label>備考欄</Label>
                      <Textarea {...register('remarks.notes')} isError={!!errors.remarks?.notes} />
                      {errors.remarks?.notes?.message && <ErrorMessage>{errors.remarks.notes.message}</ErrorMessage>}
                    </div>
                  </Body>
                </Panel>

                <DateField
                  name="remarks.ownerCertifiedOn"
                  label="事業主証明年月日"
                  error={errors.remarks?.ownerCertifiedOn?.message}
                />

                <Panel>
                  <Head>申請者欄</Head>
                  <Body className={styles.formBody}>
                    <DateField name="remarks.submitOn" label="提出年月日" error={errors.remarks?.submitOn?.message} />

                    <div className={styles.field}>
                      <Label>申請者氏名</Label>
                      <Note>
                        同意書を有する場合等、被保険者の（電子）署名が省略できる場合には以下にチェックを入れてください。
                      </Note>
                      <Checkbox {...register('remarks.isApplicantConsented')}>申請について同意済み</Checkbox>
                    </div>
                  </Body>
                </Panel>

                <div className={styles.field}>
                  <p className="u-mb10">
                    ※ 平成30年5月以降、マイナンバー（個人番号）の記載・添付が必須になります。
                    <br />
                    出力・ダウンロードの前に「マイナンバー出力」ボタンを押すか、印刷後に手でマイナンバーを記入してください。
                  </p>
                  <p>マイナンバーを添付せず電子申請を行う場合以下にチェックを入れてください。</p>
                  <Checkbox {...register('remarks.ignoreMyNumber')}>本人事由によりマイナンバー届出不可</Checkbox>
                </div>

                <LaborConsultantSection
                  procedureStatusId={procedureStatus.id}
                  reportName="R512"
                  prefix="laborConsultantForm."
                />
              </>
            )}
          </Body>
        </Panel>

        <div className="u-ta-c u-mt30">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={confirmUpgrade} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>

      {confirmUpgradeModal}
    </FormProvider>
  )
}
